import React, { useState } from 'react';
import { MapPin, Phone, Mail, Clock, Send } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ContactPage() {
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await axios.post(`${BACKEND_URL}/api/contact`, {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        subject: formData.subject,
        message: formData.message
      });

      toast.success('Message envoyé !', {
        description: 'Nous vous répondrons dans les plus brefs délais.'
      });

      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      console.error('Contact error:', error);
      toast.error('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setIsLoading(false);
    }
  };

  const pageTitle = "Contact | Léger's Coffee Genève";
  const pageDescription =
    "Contactez Léger's Coffee à Genève : adresse, téléphone, email, horaires d'ouverture et formulaire de contact pour vos questions, réservations ou demandes de partenariat.";
  const canonicalUrl = 'https://legerscoffee.fr/contact';
  const ogImage = 'https://legerscoffee.fr/og-image.jpg';

  const structuredDataContactPage = {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: "Contact | Léger's Coffee",
    url: canonicalUrl,
    description: pageDescription,
    inLanguage: 'fr-CH'
  };

  const structuredDataBusiness = {
    '@context': 'https://schema.org',
    '@type': 'CafeOrCoffeeShop',
    name: "Léger's Coffee",
    url: 'https://legerscoffee.fr/',
    image: [ogImage],
    telephone: '+41221234567',
    email: 'contact@legers-coffee.com',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Pl. des Augustins 19',
      postalCode: '1205',
      addressLocality: 'Genève',
      addressCountry: 'CH'
    },
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: 'Monday',
        opens: '07:00',
        closes: '20:30'
      },
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: 'Tuesday',
        opens: '07:00',
        closes: '20:30'
      },
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: 'Wednesday',
        opens: '07:00',
        closes: '20:30'
      },
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: 'Thursday',
        opens: '07:00',
        closes: '20:30'
      },
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: 'Friday',
        opens: '07:00',
        closes: '17:00'
      },
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: 'Saturday',
        opens: '07:00',
        closes: '14:00'
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={canonicalUrl} />

        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Léger's Coffee" />
        <meta property="og:locale" content="fr_CH" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={ogImage} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={ogImage} />

        <script type="application/ld+json">
          {JSON.stringify(structuredDataContactPage)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(structuredDataBusiness)}
        </script>
      </Helmet>

      <main data-testid="contact-page" className="min-h-screen pt-20">
        {/* Hero */}
        <section className="bg-warm-secondary py-16 lg:py-24">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="text-center max-w-2xl mx-auto">
              <span className="text-latte-gold text-sm uppercase tracking-widest font-medium mb-4 block">
                Contactez-Nous
              </span>
              <h1 className="font-serif text-4xl lg:text-5xl text-espresso font-semibold mb-6">
                Nous Sommes à Votre Écoute
              </h1>
              <p className="text-soft-charcoal-secondary">
                Une question ? Une suggestion ? N'hésitez pas à nous contacter.
              </p>
            </div>
          </div>
        </section>

        <section className="section-padding bg-warm-alabaster">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
              {/* Contact Info */}
              <div>
                <h2 className="font-serif text-2xl text-espresso font-semibold mb-8">
                  Informations
                </h2>

                <div className="space-y-8">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-warm-secondary rounded-full flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5 text-latte-gold" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-espresso mb-1">Adresse</h3>
                      <p className="text-soft-charcoal-secondary">
                        Pl. des Augustins 19<br />
                        1205 Genève, Suisse
                      </p>
                      <a
                        href="https://maps.google.com/?q=Place+des+Augustins+19+1205+Genève+Suisse"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-block mt-2 text-latte-gold hover:underline text-sm"
                      >
                        Obtenir l'itinéraire →
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-warm-secondary rounded-full flex items-center justify-center flex-shrink-0">
                      <Clock className="w-5 h-5 text-latte-gold" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-espresso mb-1">Horaires</h3>
                      <div className="text-soft-charcoal-secondary text-sm space-y-1">
                        <p>Lundi : 07:00 – 20:30</p>
                        <p>Mardi : 07:00 – 20:30</p>
                        <p>Mercredi : 07:00 – 20:30</p>
                        <p>Jeudi : 07:00 – 20:30</p>
                        <p>Vendredi : 07:00 – 17:00</p>
                        <p>Samedi : 07:00 – 14:00</p>
                        <p className="text-red-500">Dimanche : Fermé</p>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-warm-secondary rounded-full flex items-center justify-center flex-shrink-0">
                      <Phone className="w-5 h-5 text-latte-gold" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-espresso mb-1">Téléphone</h3>
                      <a
                        href="tel:+41782215200"
                        className="text-soft-charcoal-secondary hover:text-latte-gold transition-colors duration-300"
                      >
                        +41 78 221 52 00
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-warm-secondary rounded-full flex items-center justify-center flex-shrink-0">
                      <Phone className="w-5 h-5 text-latte-gold" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-espresso mb-1">Fax</h3>
                      <a
                        href="tel:+41223135509"
                        className="text-soft-charcoal-secondary hover:text-latte-gold transition-colors duration-300"
                      >
                        +41 22 313 53 62
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-warm-secondary rounded-full flex items-center justify-center flex-shrink-0">
                      <Mail className="w-5 h-5 text-latte-gold" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-espresso mb-1">Email</h3>
                      <a
                        href="mailto:contact@legers-coffee.com"
                        className="text-soft-charcoal-secondary hover:text-latte-gold transition-colors duration-300"
                      >
                        contact@legers-coffee.com
                      </a>
                    </div>
                  </div>
                </div>

                {/* Map */}
                <div className="mt-12 aspect-video bg-warm-secondary">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2761.8458831684!2d6.1422!3d46.2001!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x478c652e!2sPlace%20des%20Augustins%2019%2C%201205%20Gen%C3%A8ve%2C%20Switzerland!5e0!3m2!1sfr!2sch!4v1234567890"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Léger's Coffee Location"
                  />
                </div>
              </div>

              {/* Contact Form */}
              <div>
                <div className="bg-white p-8 border border-border-subtle">
                  <h2 className="font-serif text-2xl text-espresso font-semibold mb-6">
                    Envoyez-nous un Message
                  </h2>

                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="firstName">Prénom *</Label>
                        <Input
                          id="firstName"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleInputChange}
                          required
                          className="input-field"
                          data-testid="contact-firstname"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="lastName">Nom *</Label>
                        <Input
                          id="lastName"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleInputChange}
                          required
                          className="input-field"
                          data-testid="contact-lastname"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="input-field"
                        data-testid="contact-email"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="phone">Téléphone</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="input-field"
                        data-testid="contact-phone"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="subject">Sujet *</Label>
                      <Select
                        value={formData.subject}
                        onValueChange={(value) => setFormData(prev => ({ ...prev, subject: value }))}
                      >
                        <SelectTrigger className="h-12 rounded-none border-border-subtle" data-testid="contact-subject">
                          <SelectValue placeholder="Sélectionnez un sujet" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="general">Question générale</SelectItem>
                          <SelectItem value="order">Commande</SelectItem>
                          <SelectItem value="reservation">Réservation</SelectItem>
                          <SelectItem value="partnership">Partenariat</SelectItem>
                          <SelectItem value="feedback">Retour d'expérience</SelectItem>
                          <SelectItem value="other">Autre</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="message">Message *</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        required
                        rows={5}
                        className="border-border-subtle focus:border-espresso rounded-none resize-none"
                        placeholder="Comment pouvons-nous vous aider ?"
                        data-testid="contact-message"
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={isLoading}
                      className="w-full btn-primary"
                      data-testid="contact-submit"
                    >
                      {isLoading ? (
                        'Envoi en cours...'
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Envoyer le Message
                        </>
                      )}
                    </Button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}